import { createClient } from "@supabase/supabase-js";

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

if (!url || !anonKey) {
  throw new Error(
    "Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. " +
      "Set them in frontend/.env.local (see .env.example).",
  );
}

/**
 * SPEC-16 — "Beni hatırla" (Remember me).
 *
 * Supabase fixes the persistence storage at client-creation time, so we
 * give it a hybrid adapter and route every auth-token read/write to
 * either `localStorage` (persist across browser restarts — default) or
 * `sessionStorage` (cleared when the tab/browser closes — Remember me
 * unchecked).
 *
 * The user's choice itself is a small flag stored in `localStorage`
 * under {@link REMEMBER_KEY}, so that a Remember-me=false session
 * survives in-tab page reloads (sessionStorage still has the token,
 * the flag still says "stay non-persistent") but disappears on
 * browser restart (sessionStorage is empty, localStorage never carried
 * the token).
 */
const REMEMBER_KEY = "ifrs16:auth:remember";

let rememberMe: boolean = (() => {
  try {
    return localStorage.getItem(REMEMBER_KEY) !== "false";
  } catch {
    return true;
  }
})();

/**
 * Set the persistence mode for the NEXT auth-token write. Call this
 * immediately before `supabase.auth.signInWithPassword(...)` so the
 * token landing from that login is routed to the right storage. The
 * preference is also persisted (as a small flag, not the token) so the
 * choice survives page reloads.
 */
export function setRememberMe(value: boolean): void {
  rememberMe = value;
  try {
    localStorage.setItem(REMEMBER_KEY, value ? "true" : "false");
  } catch {
    // localStorage unavailable (private mode, quota); the in-memory
    // flag still drives session routing for the current page life.
  }
}

const hybridStorage = {
  getItem(key: string): string | null {
    try {
      // Prefer sessionStorage so a Remember-me=false session survives
      // page reloads in the same tab; fall back to localStorage for
      // standard persisted sessions.
      const fromSession = sessionStorage.getItem(key);
      if (fromSession != null) return fromSession;
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem(key: string, value: string): void {
    try {
      if (rememberMe) {
        localStorage.setItem(key, value);
        sessionStorage.removeItem(key);
      } else {
        sessionStorage.setItem(key, value);
        localStorage.removeItem(key);
      }
    } catch {
      // Storage unavailable — silently drop so app boot does not crash.
    }
  },
  removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    } catch {
      // ignore
    }
  },
};

export const supabase = createClient(url, anonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    storage: hybridStorage,
  },
});

export const ENGAGEMENTS_TABLE = "engagements";
